import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Maximize, Maximize2, Minimize2, Monitor, Smartphone, X } from 'lucide-react';
import ConfettiCanvas from './ConfettiCanvas';
import { SlotReels, useSlotRoll } from './SlotReel';
import {
  FRAMES,
  NAME_MODES,
  formatWinnerName,
  hasWinner,
  sortForDraw,
  usePrefersReducedMotion,
} from './liveDrawUtils';
import { getError } from '../../../utils';
import './sorteoEnVivo.css';

const GOLD = '#ffd54a';
const COUNT_STEP_MS = 900;
const CONTROLS_HIDE_MS = 2500;

function stageBoxStyle(frame) {
  const ratio = FRAMES[frame]?.ratio;
  if (!ratio) return { width: '100%', height: '100%' };
  return {
    width: `min(100vw, calc(100vh * ${ratio}))`,
    height: `min(100vh, calc(100vw / ${ratio}))`,
  };
}

/**
 * Full-screen presentation stage for the live draw — designed to be screen-recorded
 * (works at 9:16 and 16:9). The winner is always picked by the server; this component
 * only animates towards the number it returns.
 */
export default function DrawStage({
  rifa,
  sorteo,
  premios,
  eligibleNumbers,
  initialPremioId,
  nameMode,
  onNameModeChange,
  frame,
  onFrameChange,
  onDraw,
  onClose,
}) {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const outerRef = useRef(null);
  const stageRef = useRef(null);

  const ordered = useMemo(() => sortForDraw(premios), [premios]);
  const [currentId, setCurrentId] = useState(
    () => initialPremioId || ordered.find(p => !hasWinner(p))?._id || ordered[0]?._id || null
  );
  const current = ordered.find(p => p._id === currentId) || null;

  const [phase, setPhase] = useState('idle'); // idle | countdown | rolling | reveal | finished
  const [count, setCount] = useState(3);
  const [pool, setPool] = useState([]);
  const [result, setResult] = useState(null); // { nroSerial, nombre, anonimo }
  const [error, setError] = useState('');
  const [confettiKey, setConfettiKey] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [size, setSize] = useState({ w: 1280, h: 720 });

  const runRef = useRef(0); // invalidates timers/promises of an aborted draw
  const timersRef = useRef([]);
  const hideTimerRef = useRef(0);

  // ── Stage size (drives typography + portrait/landscape layout) ──────────
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.max(1, r.width), h: Math.max(1, r.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frame]);

  // ── Fullscreen ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      outerRef.current?.requestFullscreen?.().catch(() => {});
    }
  }, []);

  const close = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    onClose();
  }, [onClose]);

  // ── Auto-hiding controls + cursor (clean recordings) ────────────────────
  const pokeControls = useCallback(() => {
    setControlsVisible(true);
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
  }, []);

  useEffect(() => {
    pokeControls();
    return () => window.clearTimeout(hideTimerRef.current);
  }, [pokeControls]);

  // ── Cleanup timers on unmount ───────────────────────────────────────────
  useEffect(
    () => () => {
      runRef.current += 1;
      timersRef.current.forEach(id => window.clearTimeout(id));
    },
    []
  );

  // ── Draw flow ───────────────────────────────────────────────────────────
  const startDraw = useCallback(() => {
    if (phase !== 'idle' || !current || hasWinner(current)) return;
    const runId = ++runRef.current;
    timersRef.current.forEach(id => window.clearTimeout(id));
    timersRef.current = [];

    setPool(eligibleNumbers || []);
    setResult(null);
    setError('');
    setCount(3);
    setPhase('countdown');

    // Ask the server right away; the countdown hides the latency.
    Promise.resolve(onDraw(current))
      .then(res => {
        if (runRef.current !== runId) return;
        setResult({
          nroSerial: res?.ganador?.nroSerial ?? res?.premio?.nroGanador,
          nombre: res?.ganador?.nombre ?? res?.premio?.ganadorNombre ?? null,
          anonimo: Boolean(res?.ganador?.anonimo),
        });
      })
      .catch(err => {
        if (runRef.current !== runId) return;
        runRef.current += 1;
        timersRef.current.forEach(id => window.clearTimeout(id));
        const code = err?.response?.data?.code;
        const known = {
          NO_ELIGIBLE_TICKETS: 'liveDraw.errors.noEligible',
          ALREADY_DRAWN: 'liveDraw.errors.alreadyDrawn',
        };
        setError(code && known[code] ? t(known[code]) : getError(err));
        setPhase('idle');
      });

    timersRef.current.push(window.setTimeout(() => setCount(2), COUNT_STEP_MS));
    timersRef.current.push(window.setTimeout(() => setCount(1), COUNT_STEP_MS * 2));
    timersRef.current.push(
      window.setTimeout(() => {
        if (runRef.current === runId) setPhase('rolling');
      }, COUNT_STEP_MS * 3)
    );
  }, [phase, current, eligibleNumbers, onDraw, t]);

  const onRollDone = useCallback(() => {
    setPhase('reveal');
    setConfettiKey(k => k + 1);
  }, []);

  const roll = useSlotRoll({
    active: phase === 'rolling',
    pool,
    winner: result?.nroSerial,
    reducedMotion,
    onDone: onRollDone,
  });

  const pending = ordered.filter(p => !hasWinner(p) && p._id !== currentId);
  const goNext = useCallback(() => {
    if (phase === 'countdown' || phase === 'rolling') return;
    const next = ordered.find(p => !hasWinner(p));
    setResult(null);
    setError('');
    if (next) {
      setCurrentId(next._id);
      setPhase('idle');
    } else {
      setPhase('finished');
      setConfettiKey(k => k + 1);
    }
  }, [ordered, phase]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => {
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === ' ' || k === 'enter') {
        e.preventDefault();
        if (phase === 'idle' && current && !hasWinner(current)) startDraw();
        else if (phase === 'reveal' || (phase === 'idle' && hasWinner(current))) goNext();
      } else if (k === 'f') {
        toggleFullscreen();
      } else if (k === 'n') {
        const i = NAME_MODES.indexOf(nameMode);
        onNameModeChange(NAME_MODES[(i + 1) % NAME_MODES.length]);
      } else if (k === 'escape' && !document.fullscreenElement) {
        close();
      }
      pokeControls();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    phase,
    current,
    startDraw,
    goNext,
    toggleFullscreen,
    nameMode,
    onNameModeChange,
    close,
    pokeControls,
  ]);

  // ── Derived display ─────────────────────────────────────────────────────
  const portrait = size.h > size.w * 1.05;
  const u = Math.min(size.w, size.h) / 100; // 1% of the short side, in px

  const showingStoredWinner = phase === 'idle' && current && hasWinner(current);
  const revealNumber =
    phase === 'reveal' ? result?.nroSerial : showingStoredWinner ? current.nroGanador : null;
  const revealName =
    phase === 'reveal' ? result?.nombre : showingStoredWinner ? current.ganadorNombre : null;
  const revealAnon = phase === 'reveal' ? result?.anonimo : false;
  const displayName = formatWinnerName(revealName, nameMode);

  const reelLen =
    phase === 'rolling'
      ? roll.len
      : Math.max(
          1,
          ...(eligibleNumbers || []).map(n => String(n).length),
          ...(pool || []).map(n => String(n).length),
          revealNumber ? String(revealNumber).length : 1
        );
  let reelChars;
  if (phase === 'rolling') reelChars = roll.chars;
  else if (revealNumber) reelChars = String(revealNumber).padStart(reelLen, ' ').split('');
  else reelChars = Array(reelLen).fill('?');

  const board = sortForDraw(premios)
    .filter(p => hasWinner(p))
    .filter(p => !(p._id === currentId && (phase === 'countdown' || phase === 'rolling')))
    .sort((a, b) => (Number(a.puesto) || 0) - (Number(b.puesto) || 0));
  const showBoard = board.length > 0 && phase !== 'countdown';

  const mainWidth = portrait || !showBoard ? size.w : size.w * 0.68;
  const reelFont = Math.min(
    (mainWidth * 0.86) / (reelLen * 1.06),
    size.h * (portrait ? 0.11 : 0.19)
  );

  const prizeLabel = current ? t('liveDraw.placeLabel', { n: current.puesto }) : '';
  const isBusy = phase === 'countdown' || phase === 'rolling';
  const showAction =
    (phase === 'idle' && current && !hasWinner(current)) ||
    ((phase === 'reveal' || showingStoredWinner) && controlsVisible);

  const nameModeLabel = {
    full: t('liveDraw.nameFull'),
    short: t('liveDraw.nameShort'),
    hidden: t('liveDraw.nameHidden'),
  }[nameMode];

  const stage = (
    <div
      ref={outerRef}
      className={`fixed inset-0 flex items-center justify-center bg-black ${
        controlsVisible || phase === 'idle' ? '' : 'sev-cursor-hidden'
      }`}
      style={{ zIndex: 2000 }}
      onMouseMove={pokeControls}
      onTouchStart={pokeControls}
      role='dialog'
      aria-modal='true'
      aria-label={t('liveDraw.title')}
    >
      <div ref={stageRef} className='sev-stage' style={stageBoxStyle(frame)}>
        {rifa?.imageUrl && (
          <div className='sev-bg-image' style={{ backgroundImage: `url("${rifa.imageUrl}")` }} />
        )}
        <div className={`sev-rays ${phase === 'rolling' ? 'sev-fast' : ''}`} />
        <ConfettiCanvas fireKey={confettiKey} disabled={reducedMotion} />

        <div
          className='relative flex h-full w-full'
          style={{
            flexDirection: portrait ? 'column' : 'row',
            padding: `${u * 5}px ${u * 5}px`,
            gap: `${u * 3}px`,
            zIndex: 10,
          }}
        >
          {/* ── Main column ── */}
          <div className='flex min-h-0 min-w-0 flex-1 flex-col items-center' style={{ gap: u * 2 }}>
            {/* Header */}
            <header
              className='flex w-full flex-col items-center text-center'
              style={{ gap: u * 1.2 }}
            >
              <div className='flex items-center' style={{ gap: u * 2 }}>
                {rifa?.imageUrl && (
                  <img
                    src={rifa.imageUrl}
                    alt=''
                    className='shrink-0 object-cover shadow-lg'
                    style={{
                      width: u * (portrait ? 11 : 9),
                      height: u * (portrait ? 11 : 9),
                      borderRadius: u * 2,
                      border: `${Math.max(2, u * 0.4)}px solid rgba(255,255,255,0.85)`,
                    }}
                  />
                )}
                <h1
                  className='m-0 font-black uppercase leading-none tracking-tight text-white'
                  style={{
                    fontSize: u * (portrait ? 6.4 : 5.6),
                    textShadow: '0 0.06em 0.2em rgba(0,0,0,0.5)',
                  }}
                >
                  {rifa?.nombre}
                </h1>
              </div>
              <div
                className='flex items-center font-semibold uppercase text-white/85'
                style={{
                  gap: u * 1.5,
                  fontSize: u * (portrait ? 3 : 2.4),
                  letterSpacing: '0.08em',
                }}
              >
                <span
                  className='inline-flex items-center rounded-full bg-red-600/90 font-extrabold text-white'
                  style={{ gap: u * 0.8, padding: `${u * 0.4}px ${u * 1.4}px` }}
                >
                  <span className='sev-live-dot' />
                  {t('liveDraw.liveBadge')}
                </span>
                <span className='truncate'>{sorteo?.titulo}</span>
              </div>
            </header>

            {/* Center */}
            <div
              className='flex w-full min-h-0 flex-1 flex-col items-center justify-center text-center'
              style={{ gap: u * 2.2 }}
            >
              {phase === 'finished' ? (
                <div className='sev-pop'>
                  <p
                    className='m-0 font-black uppercase leading-tight sev-glow-text'
                    style={{ color: GOLD, fontSize: u * (portrait ? 7 : 6.5) }}
                  >
                    {t('liveDraw.allDrawnTitle')}
                  </p>
                  <p
                    className='m-0 font-semibold text-white/85'
                    style={{ fontSize: u * 3, marginTop: u * 1.5 }}
                  >
                    {t('liveDraw.thanks')}
                  </p>
                </div>
              ) : (
                <>
                  {current && (
                    <div
                      key={currentId}
                      className='sev-fade-up flex flex-col items-center'
                      style={{ gap: u * 0.6 }}
                    >
                      <span
                        className='rounded-full font-extrabold uppercase'
                        style={{
                          background: GOLD,
                          color: '#1a1300',
                          fontSize: u * (portrait ? 3.2 : 2.6),
                          padding: `${u * 0.5}px ${u * 2.2}px`,
                          letterSpacing: '0.1em',
                        }}
                      >
                        {prizeLabel}
                      </span>
                      <h2
                        className='m-0 font-black leading-tight'
                        style={{ fontSize: u * (portrait ? 7.2 : 6.2), maxWidth: mainWidth * 0.92 }}
                      >
                        {current.nombre}
                      </h2>
                      {current.descripcion && (
                        <p
                          className='m-0 text-white/70'
                          style={{
                            fontSize: u * (portrait ? 2.8 : 2.2),
                            maxWidth: mainWidth * 0.85,
                          }}
                        >
                          {current.descripcion}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Number area */}
                  <div
                    className='relative flex items-center justify-center'
                    style={{ minHeight: reelFont * 1.45 }}
                  >
                    {phase === 'countdown' ? (
                      <div className='flex flex-col items-center'>
                        <span
                          className='font-bold uppercase text-white/80'
                          style={{ fontSize: u * 3, letterSpacing: '0.15em' }}
                        >
                          {t('liveDraw.getReady')}
                        </span>
                        <span
                          key={count}
                          className='sev-count block font-black leading-none sev-glow-text'
                          style={{ color: GOLD, fontSize: Math.min(reelFont * 1.5, size.h * 0.24) }}
                        >
                          {count}
                        </span>
                      </div>
                    ) : (
                      <SlotReels
                        chars={reelChars}
                        locked={phase === 'rolling' ? roll.locked : 0}
                        tick={roll.tick}
                        winnerMode={Boolean(revealNumber)}
                        idle={!revealNumber && phase !== 'rolling'}
                        fontSize={reelFont}
                      />
                    )}
                  </div>

                  {/* Status / winner */}
                  <div style={{ minHeight: u * 11 }} className='flex flex-col items-center'>
                    {phase === 'rolling' && (
                      <span
                        className='font-bold uppercase text-white/80'
                        style={{ fontSize: u * 3, letterSpacing: '0.2em' }}
                      >
                        {t('liveDraw.rolling')}
                      </span>
                    )}
                    {revealNumber && (
                      <>
                        <span
                          key={`w-${revealNumber}`}
                          className='sev-pop font-black uppercase leading-none sev-glow-text'
                          style={{ color: GOLD, fontSize: u * (portrait ? 8 : 6.5) }}
                        >
                          {t('liveDraw.winner')}
                        </span>
                        {displayName ? (
                          <span
                            className='sev-fade-up font-extrabold text-white'
                            style={{
                              fontSize: u * (portrait ? 5.2 : 4.4),
                              marginTop: u * 1,
                              animationDelay: '250ms',
                            }}
                          >
                            {displayName}
                          </span>
                        ) : (
                          revealAnon &&
                          nameMode !== 'hidden' && (
                            <span
                              className='sev-fade-up text-white/80'
                              style={{ fontSize: u * 3.4, marginTop: u * 1 }}
                            >
                              {t('liveDraw.anonymousBuyer')}
                            </span>
                          )
                        )}
                        <span
                          className='text-white/60'
                          style={{ fontSize: u * 2.2, marginTop: u * 0.8 }}
                        >
                          {t('liveDraw.ticketNumber')} {revealNumber}
                        </span>
                      </>
                    )}
                    {error && phase === 'idle' && (
                      <span
                        className='rounded-lg bg-red-600/90 font-semibold text-white'
                        style={{ fontSize: u * 2.4, padding: `${u * 0.8}px ${u * 2}px` }}
                      >
                        {error}
                      </span>
                    )}
                  </div>
                  {/* Big action button (in flow; space reserved so nothing jumps) */}
                  <div
                    className={`sev-controls flex justify-center ${
                      showAction && !isBusy ? '' : 'sev-hidden'
                    }`}
                    style={{ minHeight: u * 9 }}
                  >
                    <button
                      type='button'
                      tabIndex={showAction && !isBusy ? 0 : -1}
                      onClick={phase === 'idle' && !hasWinner(current) ? startDraw : goNext}
                      className='rounded-full font-black uppercase shadow-2xl transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/70'
                      style={{
                        background: `linear-gradient(180deg, #ffe58a 0%, ${GOLD} 45%, #e0a800 100%)`,
                        color: '#1a1300',
                        fontSize: u * (portrait ? 4.4 : 3.6),
                        padding: `${u * 1.6}px ${u * 6}px`,
                        letterSpacing: '0.08em',
                        border: 'none',
                      }}
                    >
                      {phase === 'idle' && !hasWinner(current)
                        ? t('liveDraw.drawAction')
                        : pending.length > 0
                          ? t('liveDraw.nextPrize')
                          : t('liveDraw.finish')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Winners board ── */}
          {showBoard && (
            <aside
              className='flex min-h-0 flex-col overflow-hidden rounded-2xl border border-white/15 bg-black/35 backdrop-blur-sm'
              style={{
                width: portrait ? '100%' : '30%',
                maxHeight: portrait ? '30%' : '100%',
                alignSelf: portrait ? 'stretch' : 'center',
                padding: u * 2,
                gap: u * 1,
              }}
            >
              <h3
                className='m-0 font-extrabold uppercase'
                style={{
                  color: GOLD,
                  fontSize: u * (portrait ? 2.8 : 2.4),
                  letterSpacing: '0.12em',
                }}
              >
                {t('liveDraw.winnersBoard')}
              </h3>
              <ol
                className='m-0 flex min-h-0 flex-col overflow-hidden p-0'
                style={{ gap: u * 0.8, listStyle: 'none' }}
              >
                {board.map(p => {
                  const name = formatWinnerName(p.ganadorNombre, nameMode);
                  const highlight = p._id === currentId;
                  return (
                    <li
                      key={p._id}
                      className={`flex items-center rounded-xl ${highlight ? 'sev-fade-up' : ''}`}
                      style={{
                        gap: u * 1.2,
                        padding: `${u * 0.8}px ${u * 1.2}px`,
                        background: highlight ? 'rgba(255,213,74,0.18)' : 'rgba(255,255,255,0.06)',
                        border: highlight ? `1px solid ${GOLD}` : '1px solid transparent',
                      }}
                    >
                      <span
                        className='shrink-0 rounded-full text-center font-black'
                        style={{
                          background: GOLD,
                          color: '#1a1300',
                          fontSize: u * 2,
                          minWidth: u * 4.6,
                          padding: `${u * 0.3}px ${u * 0.6}px`,
                        }}
                      >
                        {p.puesto}º
                      </span>
                      <span className='min-w-0 flex-1'>
                        <span
                          className='block truncate font-bold text-white'
                          style={{ fontSize: u * 2.2 }}
                        >
                          {p.nombre}
                        </span>
                        {name && (
                          <span
                            className='block truncate text-white/70'
                            style={{ fontSize: u * 1.8 }}
                          >
                            {name}
                          </span>
                        )}
                      </span>
                      <span
                        className='shrink-0 font-black tabular-nums'
                        style={{ color: GOLD, fontSize: u * 2.8 }}
                      >
                        {p.nroGanador}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </aside>
          )}
        </div>
      </div>

      {/* ── Controls (auto-hide; outside the framed stage when letterboxed) ── */}
      <div
        className={`sev-controls absolute right-0 top-0 flex flex-wrap items-center justify-end gap-2 p-3 ${
          controlsVisible ? '' : 'sev-hidden'
        }`}
        style={{ zIndex: 50 }}
      >
        <ControlButton
          onClick={() =>
            onNameModeChange(NAME_MODES[(NAME_MODES.indexOf(nameMode) + 1) % NAME_MODES.length])
          }
          title={t('liveDraw.nameMode')}
        >
          {nameMode === 'hidden' ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
          <span className='hidden sm:inline'>{nameModeLabel}</span>
        </ControlButton>
        <div className='flex overflow-hidden rounded-full bg-white/10 backdrop-blur'>
          {[
            ['fill', Maximize, t('liveDraw.frameFill')],
            ['vertical', Smartphone, t('liveDraw.frameVertical')],
            ['horizontal', Monitor, t('liveDraw.frameHorizontal')],
          ].map(([key, Icon, label]) => (
            <button
              key={key}
              type='button'
              title={label}
              aria-label={label}
              aria-pressed={frame === key}
              onClick={() => onFrameChange(key)}
              className={`flex h-9 w-10 items-center justify-center text-white ${
                frame === key ? 'bg-white/30' : 'bg-transparent hover:bg-white/15'
              }`}
            >
              <Icon className='h-4 w-4' />
            </button>
          ))}
        </div>
        <ControlButton onClick={toggleFullscreen} title={t('liveDraw.fullscreen')}>
          {isFullscreen ? <Minimize2 className='h-4 w-4' /> : <Maximize2 className='h-4 w-4' />}
          <span className='hidden sm:inline'>
            {isFullscreen ? t('liveDraw.exitFullscreen') : t('liveDraw.fullscreen')}
          </span>
        </ControlButton>
        <ControlButton onClick={close} title={t('liveDraw.exit')}>
          <X className='h-4 w-4' />
        </ControlButton>
      </div>
      <div
        className={`sev-controls pointer-events-none absolute bottom-2 left-0 right-0 text-center text-xs text-white/60 ${
          controlsVisible ? '' : 'sev-hidden'
        }`}
        style={{ zIndex: 50 }}
      >
        {t('liveDraw.shortcuts')}
      </div>
    </div>
  );

  return createPortal(stage, document.body);
}

function ControlButton({ children, onClick, title }) {
  return (
    <button
      type='button'
      onClick={onClick}
      title={title}
      aria-label={title}
      className='flex h-9 items-center gap-2 rounded-full bg-white/10 px-3 text-sm font-semibold text-white backdrop-blur hover:bg-white/20'
    >
      {children}
    </button>
  );
}
